import { useCallback, useRef, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useLogout, useSession } from "../hooks/useSession.js";
import { ChangePasswordDialog } from "./ChangePasswordDialog.js";
import { AdminMobileNav } from "./AdminMobileNav.js";
import { Button } from "./ui.js";

export function AppLayout() {
  const session = useSession();
  const logout = useLogout();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDetailsElement>(null);
  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);

  return (
    <div className="flex min-h-full flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-slate-950 focus:p-3 focus:text-white">Skip to content</a>
      <header className="sticky top-0 z-40 border-b border-slate-800 bg-slate-950/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 px-3 py-2 sm:gap-3 sm:px-4 sm:py-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3 lg:gap-6">
            <Button
              type="button"
              variant="secondary"
              className="h-10 w-10 shrink-0 px-0 lg:hidden"
              aria-label="Open navigation menu"
              aria-controls="admin-mobile-nav"
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(true)}
            >
              <span aria-hidden="true" className="text-lg leading-none">☰</span>
            </Button>
            <div className="flex items-center gap-2">
              <img
                src="/logo-mark.svg"
                alt=""
                className="h-8 w-8 rounded-md bg-white p-0.5"
              />
              <img
                src="/logo-text.webp"
                alt="Farnsworth Collectibles"
                className="hidden h-7 w-auto sm:block"
              />
              <span className="rounded-full border border-slate-700 px-2 py-0.5 text-[10px] uppercase tracking-wide text-slate-400">
                Admin
              </span>
            </div>
            <nav aria-label="Primary" className="hidden max-w-full gap-1 overflow-x-auto text-sm lg:flex">
              <NavLink
                to="/"
                end
                className={({ isActive }) => isActive ? "whitespace-nowrap rounded-md bg-indigo-950 px-2 py-1 text-indigo-300" : "whitespace-nowrap rounded-md px-2 py-1 text-slate-400 hover:bg-slate-900 hover:text-slate-200"}
              >
                Inventory
              </NavLink>
              <NavLink
                to="/reports"
                className={({ isActive }) => isActive ? "whitespace-nowrap rounded-md bg-indigo-950 px-2 py-1 text-indigo-300" : "whitespace-nowrap rounded-md px-2 py-1 text-slate-400 hover:bg-slate-900 hover:text-slate-200"}
              >
                Reports
              </NavLink>
              {session.data?.role === "admin" || session.data?.role === "manager" ? (
                <NavLink to="/whatnot" className={({ isActive }) => isActive ? "whitespace-nowrap rounded-md bg-indigo-950 px-2 py-1 text-indigo-300" : "whitespace-nowrap rounded-md px-2 py-1 text-slate-400 hover:bg-slate-900 hover:text-slate-200"}>
                  Whatnot
                </NavLink>
              ) : null}
              {session.data?.role === "admin" || session.data?.role === "manager" ? (
                <NavLink
                  to="/categories"
                  className={({ isActive }) => isActive ? "whitespace-nowrap rounded-md bg-indigo-950 px-2 py-1 text-indigo-300" : "whitespace-nowrap rounded-md px-2 py-1 text-slate-400 hover:bg-slate-900 hover:text-slate-200"}
                >
                  Categories
                </NavLink>
              ) : null}
              {session.data?.role === "admin" || session.data?.role === "manager" ? <NavLink to="/retailers" className="whitespace-nowrap rounded-md px-2 py-1 text-slate-400 hover:bg-slate-900 hover:text-slate-200">Retailers</NavLink> : null}
              {session.data?.role === "admin" || session.data?.role === "manager" ? (
                <NavLink
                  to="/messages"
                  className={({ isActive }) => isActive ? "whitespace-nowrap rounded-md bg-indigo-950 px-2 py-1 text-indigo-300" : "whitespace-nowrap rounded-md px-2 py-1 text-slate-400 hover:bg-slate-900 hover:text-slate-200"}
                >
                  Messages
                </NavLink>
              ) : null}
              {session.data?.role === "admin" ? (
                <NavLink
                  to="/users"
                  className={({ isActive }) => isActive ? "whitespace-nowrap rounded-md bg-indigo-950 px-2 py-1 text-indigo-300" : "whitespace-nowrap rounded-md px-2 py-1 text-slate-400 hover:bg-slate-900 hover:text-slate-200"}
                >
                  Users
                </NavLink>
              ) : null}
              {session.data?.role === "admin" ? <>
                <NavLink to="/audit" className="whitespace-nowrap rounded-md px-2 py-1 text-slate-400 hover:bg-slate-900 hover:text-slate-200">Audit history</NavLink>
                <NavLink to="/agent-keys" className="whitespace-nowrap rounded-md px-2 py-1 text-slate-400 hover:bg-slate-900 hover:text-slate-200">Agent keys</NavLink>
              </> : null}
            </nav>
          </div>
          <div className="flex shrink-0 items-center text-sm text-slate-400">
            <details ref={accountMenuRef} className="relative">
              <summary
                aria-label="Account menu"
                className="flex cursor-pointer list-none items-center gap-2 rounded-md px-2.5 py-2 text-slate-200 hover:bg-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-400"
              >
                <span className="max-w-32 truncate">{session.data?.username ?? "Account"}</span>
                {session.data?.role ? (
                  <span className="rounded-full border border-slate-700 px-2 py-0.5 text-[10px] uppercase text-slate-400">
                    {session.data.role}
                  </span>
                ) : null}
                <span aria-hidden="true" className="text-xs text-slate-400">▾</span>
              </summary>
              <div className="absolute right-0 top-full z-50 mt-2 w-48 rounded-lg border border-slate-700 bg-slate-900 p-1.5 shadow-xl">
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={() => {
                    if (accountMenuRef.current) accountMenuRef.current.open = false;
                    setShowChangePassword(true);
                  }}
                >
                  Change password
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={() => {
                    if (accountMenuRef.current) accountMenuRef.current.open = false;
                    logout.mutate();
                  }}
                  disabled={logout.isPending}
                >
                  {logout.isPending ? "Signing out…" : "Sign out"}
                </Button>
              </div>
            </details>
          </div>
        </div>
      </header>
      <AdminMobileNav
        open={mobileMenuOpen}
        onClose={closeMobileMenu}
        role={session.data?.role}
      />
      {showChangePassword ? (
        <ChangePasswordDialog onClose={() => setShowChangePassword(false)} />
      ) : null}
      <main id="main" tabIndex={-1} className="mx-auto w-full max-w-7xl flex-1 px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}

